import React, { memo, useContext, useState } from 'react';
import { Handle, useReactFlow } from 'reactflow';
import LoadIcon from '../../Icon/LoadIcon';
import IconComment from '../../Icon/IconComment';
import './Loop.css';
import { ThemeIcon, Text, ActionIcon, Tooltip } from '@mantine/core';
import { IconEdit, IconPlayerPlay, IconTrash } from '@tabler/icons';
import { AppSettingsContext } from '../../../core/context/AppSettingsContext';
import CommentModal from '../../Modal/CommentModal/CommentModal';
import { DataFlowContext } from '../../../core/context/DataFlowContext';
import { useMantineTheme } from '@mantine/core';


const Loop = ({ id, data, isConnectable }) => {
    const { nodeActive } = useContext(AppSettingsContext);
    const { setNodes } = useReactFlow();
    const [isCommentOpen, setIsCommentOpen] = useState(false);
    const [comment, setComment] = useState(data.comment || '');
    const [commentHistory, setCommentHistory] = useState([data.comment || '']);
    const isActive = nodeActive?.id === id;
    const dataFlowContext = useContext(DataFlowContext);
    const theme = useMantineTheme();

    const handleRemoveNode = (e) => {
        e.stopPropagation();
        data.onRemoveNode(id);
    };

    const handleOpenNode = (e) => {
        e.stopPropagation();
        data.onUpdateNode(id);
    };

    const handleStartNode = (e) => {
        e.stopPropagation();
        data.onStartOneNode(id);
    };

    const handleOpenComment = (e) => {
        e.stopPropagation();
        setIsCommentOpen(true);
    };

    const handleCloseComment = (newComment) => {
        if (newComment !== undefined && newComment !== comment) {
            setCommentHistory(prev => [...prev, comment]);
            setComment(newComment);
            // Update React Flow state
            setNodes(prev => prev.map(node => node.id === id ? { ...node, data: { ...node.data, comment: newComment } } : node));
        }
        setIsCommentOpen(false);
    };

    const handleCommentChange = (newComment) => {
        if (newComment !== comment) {
            setCommentHistory(prev => [...prev, comment]);
            setComment(newComment);
            // Update React Flow state
            setNodes(prev => prev.map(node => node.id === id ? { ...node, data: { ...node.data, comment: newComment } } : node));
        }
    };

    const handleRollbackComment = () => {
        if (commentHistory.length > 0) {
            const previousComment = commentHistory[commentHistory.length - 1];
            setCommentHistory(prev => prev.slice(0, -1));
            setComment(previousComment);
            dataFlowContext.setComment(prev => ({
                ...prev,
                [id]: previousComment
            }));
        }
    };

    return (
        <div draggable={false}
        // style={{ pointerEvents: isCommentOpen ? 'none' : 'auto' }} // Vô hiệu hóa tương tác khi modal mở
        // onMouseDown={(e) => isCommentOpen && e.stopPropagation()}
        // onMouseMove={(e) => isCommentOpen && e.stopPropagation()}
        // onDragStart={(e) => e.preventDefault()}
        >
            <CommentModal
                isOpen={isCommentOpen}
                onClose={handleCloseComment}
                onRollback={handleRollbackComment}
                comment={comment}
                onCommentChange={handleCommentChange}
                hasPreviousComment={commentHistory.length > 0}
            />
            <div className={`node-loop ${isActive && 'active'}`}>
                <div className="node-loop-inside">
                    <div className="action-node">
                        <ActionIcon onClick={handleOpenComment} onDoubleClick={(e) => e.stopPropagation()} color="blue" size="sm" radius="xl">
                            <IconComment size={14} />
                        </ActionIcon>
                        <ActionIcon onClick={handleStartNode} color="green" size="sm" radius="xl">
                            <IconPlayerPlay size={14} />
                        </ActionIcon>
                        <ActionIcon onClick={handleRemoveNode} color="red" size="sm" radius="xl">
                            <IconTrash size={14} />
                        </ActionIcon>
                        <ActionIcon onClick={handleOpenNode} color="blue" size="sm" radius="xl">
                            <IconEdit size={14} />
                        </ActionIcon>
                    </div>
                    <Handle
                        id={`out-${id}`}
                        type="source"
                        position="left"
                        style={{ background: "var(--source)", left: "-32px", top: "40%" }}
                        isConnectable={isConnectable}
                    />
                    <div className="node-normal-body-loop">
                        <ThemeIcon className="node-icon">
                            <LoadIcon icon={data.icon} size={16} />
                        </ThemeIcon>
                        <Text size="0.8rem">{data.label}</Text>
                    </div>
                    <Handle
                        id={`above-${id}`}
                        type="target"
                        position="left"
                        isConnectable={isConnectable}
                        style={{ background: "var(--input)", left: "30px", top: "-50px" }}
                    />
                    <Handle
                        id={`below-${id}`}
                        type="target"
                        position="right"
                        isConnectable={isConnectable}
                        style={{ background: "var(--input)", right: "39px", top: "70px" }}
                    />
                    <Handle
                        id={`success-${id}`}
                        type="source"
                        position="right"
                        style={{ background: "var(--output)", top: "-10px" }}
                        isConnectable={isConnectable}
                    />
                    <Handle
                        id={`error-${id}`}
                        type="source"
                        position="right"
                        style={{ background: "var(--output-bottom)", top: "32px" }}
                        isConnectable={isConnectable}
                    />
                </div>
            </div>
            {comment && <div onDoubleClick={(e) => e.stopPropagation()} onClick={handleOpenComment} className="comment-node-loop">
               
                    <Tooltip
                        label={comment}
                        multiline
                        width="auto"
                        position="bottom"
                        withArrow
                        openDelay={0}
                        closeDelay={200}
                        events={{ hover: true, focus: true, touch: true }}
                        styles={{
                            tooltip: {
                                maxWidth: '500px',
                                whiteSpace: 'pre-wrap',
                                fontSize: '12px',
                                lineHeight: '1.6',
                                padding: '8px 12px',
                                background: theme.colorScheme === 'dark' ? 'rgba(37, 38, 43, 0.95)' : 'rgba(246, 242, 242, 0.95)',
                                border: '1px solid rgba(255, 255, 255, 0.1)',
                                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                                color: theme.colorScheme === 'dark' ? '#fff' : '#000',
                                maxHeight: 'none',
                                overflowY: 'hidden !important'
                            }
                        }}
                    >
                        <Text size="xs" color="dimmed" lineClamp={3} style={{
                            display: '-webkit-box',
                            WebkitLineClamp: '3',
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                        }}>
                            {comment}
                        </Text>
                    </Tooltip>
                
            </div>}
        </div>
    );
};

export default memo(Loop);